import Vue from 'vue'
import store from '@/modules/store'
import mixins from '@/modules/mixins/global'

const COOKIE_NAME = 'ordit_uinfo'
const COOKIE_EXPIRY = '30d'
const MAX_ADDRESSES = 5
const MAX_TEXT_LENGTH = 40
// Browsers silently drop cookies over ~4093 bytes (name + encoded value),
// leaving the previous, stale cookie in place — stay well below that.
const MAX_COOKIE_BYTES = 3900

/**
 * Marketing login-info cookie
 *
 * Writes a domain-level cookie with display-only data (first name, saved
 * addresses, has-budget flag) so the marketing site on the parent domain can
 * adapt its hero for logged-in users. Never stores tokens. Contract:
 * doc/2026-08-31-marketing-login-cookie-design.md
 */
class MarketingCookie {
    /**
     * The domain the cookie is scoped to, derived from the hostname:
     * *.co.uk -> last 3 labels, *.hu -> last 2 labels, anything else
     * (e.g. local dev on docker.localhost) -> null, cookie disabled.
     *
     * @return {string|null} cookie domain
     */
    get cookieDomain() {
        const hostname = window.location.hostname
        const labels = hostname.split('.')
        if (hostname.endsWith('.co.uk') && labels.length > 3) return labels.slice(-3).join('.')
        if (hostname.endsWith('.hu') && labels.length > 2) return labels.slice(-2).join('.')
        return null
    }

    /**
     * Guards the stage/prod overlap on the shared .ordit.hu domain,
     * the consumer only accepts its own environment.
     *
     * @return {string} "stage" or "prod"
     */
    get envFlag() {
        const hostname = window.location.hostname
        return hostname === 'dev.ordit.hu' || hostname.endsWith('.dev.ordit.hu') ? 'stage' : 'prod'
    }

    /**
     * Short address line for the marketing select, composed in
     * location-specific order, truncated to fit the cookie budget.
     *
     * @param {object} address address model (snake_case API fields)
     * @return {string} composed line
     */
    composeAddressLine(address) {
        const streetPart =
            address.street && address.house_number
                ? process.env.VUE_APP_LOCATION === 'HU'
                    ? `${address.street} ${address.house_number}`
                    : `${address.house_number} ${address.street}`
                : address.street || ''
        const line = streetPart
            ? [streetPart, address.zip_code].filter(Boolean).join(', ')
            : address.full_address || ''
        return this.truncate(line)
    }

    /**
     * Truncates user-entered text so oversized values cannot push the cookie
     * past the browser size limit.
     *
     * @param {string} value text to truncate
     * @return {string} at most MAX_TEXT_LENGTH characters
     */
    truncate(value) {
        if (typeof value !== 'string' || value.length <= MAX_TEXT_LENGTH) return value || ''
        return `${value.slice(0, MAX_TEXT_LENGTH - 1)}…`
    }

    /**
     * Rounds a coordinate to 6 decimals. Unlike Address#roundCoordinates it
     * returns undefined for non-numeric input (the key is then dropped by
     * JSON.stringify) instead of passing the value through — the cookie must
     * never carry garbage coordinates.
     *
     * @param {number|string} value coordinate
     * @return {number|undefined} rounded coordinate
     */
    roundCoordinate(value) {
        const num = typeof value === 'string' ? parseFloat(value) : value
        if (typeof num !== 'number' || isNaN(num)) return undefined
        return Math.round(num * 1000000) / 1000000
    }

    /**
     * Builds the cookie payload from the current user/addresses/expense rule
     * state, or null when the user is not logged in.
     *
     * @return {object|null} payload
     */
    buildPayload() {
        const user = Vue.prototype.$user
        if (!user?.isLoggedIn || !user.model?.id) return null
        const addresses = (Vue.prototype.$addresses?.items || [])
            .slice()
            .sort((a, b) => (a.position > b.position ? 1 : -1))
            .slice(0, MAX_ADDRESSES)
            .map((address) => ({
                id: address.id,
                n: this.truncate(address.name),
                a: this.composeAddressLine(address),
                lat: this.roundCoordinate(address.latitude),
                lng: this.roundCoordinate(address.longitude),
            }))
        return {
            v: 1,
            env: this.envFlag,
            li: 1,
            fn: this.truncate(user.model.first_name),
            hb: (store.getters['expenseRules'] || []).length ? 1 : 0,
            cur: mixins.getId(user.model.current_address) || undefined,
            addr: addresses,
        }
    }

    /**
     * Writes/refreshes the cookie. No-op outside the known domains or when
     * the user is not logged in. Safe to call repeatedly.
     */
    /**
     * Serialized cookie value, dropping trailing addresses while the encoded
     * size (as written by vue-cookies) would exceed the browser cookie limit.
     *
     * @param {object} payload cookie payload
     * @return {string} JSON value fitting the size budget
     */
    serializeWithinBudget(payload) {
        let value = JSON.stringify(payload)
        while (
            COOKIE_NAME.length + 1 + encodeURIComponent(value).length > MAX_COOKIE_BYTES &&
            payload.addr.length
        ) {
            payload.addr.pop()
            value = JSON.stringify(payload)
        }
        return value
    }

    sync() {
        try {
            const domain = this.cookieDomain
            if (!domain) return
            const payload = this.buildPayload()
            if (!payload) return
            Vue.$cookies.set(
                COOKIE_NAME,
                this.serializeWithinBudget(payload),
                COOKIE_EXPIRY,
                '/',
                domain,
                window.location.protocol.includes('https'),
                'Lax'
            )
        } catch (e) {
            window.debug.warn('Marketing cookie sync failed', e)
        }
    }

    /**
     * Removes the cookie (logout / auth failure). No-op outside the known
     * domains.
     */
    clear() {
        try {
            const domain = this.cookieDomain
            if (!domain) return
            Vue.$cookies.remove(COOKIE_NAME, '/', domain)
        } catch (e) {
            window.debug.warn('Marketing cookie clear failed', e)
        }
    }
}

export default new MarketingCookie()
